import React, { useMemo, useState } from 'react';
import { Navbar } from './components/Navbar';
import { ProductCard } from './components/ProductCard';
import { CartSidebar } from './components/CartSidebar';
import { HeroSlider } from './components/HeroSlider';
import { AdminDashboard } from './components/AdminDashboard';
import { ProductDetail } from './components/ProductDetail';
import { CheckoutModal } from './components/CheckoutModal';
import { MenuDrawer } from './components/MenuDrawer';
import { OrderTrackingModal } from './components/OrderTrackingModal';
import { Footer } from './components/Footer';
import { MOCK_PRODUCTS, INITIAL_BANNERS } from './constants';
import { usePersistedState } from './hooks/usePersistedState';
import { useFirestore } from './hooks/useFirestore';
import { CartItem, Product, ChatMessage, Order, Banner, Review, CategoryType, FlatDeal, TopCategory, PromoCode, UserProfile, StoreSettings, MenuItem } from './types';
import { CheckCircle, Ruler, MapPin, RotateCcw, User, LogIn, Loader2, ArrowRight, Mail, Phone, Lock, ShieldCheck } from 'lucide-react';

const DEFAULT_CATEGORIES = [
    { id: '1', name: 'Men' },
    { id: '2', name: 'Women' },
    { id: '3', name: 'Kids' },
    { id: '4', name: 'Sports' },
    { id: '5', name: 'Formal' }
];

const DEFAULT_MENU_ITEMS: MenuItem[] = [
    { id: '1', label: 'Clearance Sale', type: 'category', value: 'Sale', tag: 'Sale', tagColor: 'bg-red-600' },
    { id: '2', label: 'New Arrivals', type: 'category', value: 'All', tag: 'Hot', tagColor: 'bg-red-600' },
    { id: '3', label: 'Men', type: 'category', value: 'Men', icon: 'Plus' },
    { id: '4', label: 'Women', type: 'category', value: 'Women', icon: 'Plus' },
    { id: '5', label: 'Kids', type: 'category', value: 'Kids', icon: 'Plus' },
    { id: '6', label: 'Store Returns', type: 'category', value: 'Returns', icon: 'Plus' },
    { id: '7', label: 'Clothing', type: 'category', value: 'Clothing' },
    { id: '8', label: 'Brands', type: 'category', value: 'Brands' },
    { id: '9', label: 'Accessories', type: 'category', value: 'Accessories', icon: 'Plus' },
    { id: '10', label: 'Track Your Order', type: 'action', value: 'track_order', icon: 'Package' },
    { id: '11', label: 'Login / Register', type: 'action', value: 'login', icon: 'User' },
];

export function App() {
  // --- Firebase Data Hooks ---
  const { data: products, loading: productsLoading, add: addProduct, update: updateProduct, remove: removeProduct } = useFirestore<Product>('products', []);
  const { data: orders, loading: ordersLoading, add: addOrder, update: updateOrder } = useFirestore<Order>('orders', []);
  const { data: banners, add: addBanner, remove: removeBanner } = useFirestore<Banner>('banners', INITIAL_BANNERS);
  const { data: reviews, add: addReviewFirebase, remove: removeReview } = useFirestore<Review>('reviews', []);
  const { data: categories, add: addCategory, remove: removeCategory } = useFirestore<CategoryType>('categories', DEFAULT_CATEGORIES);
  const { data: promoCodes, add: addPromoCode, remove: removePromoCode, update: updatePromoCode } = useFirestore<PromoCode>('promocodes', []);
  const { data: users, add: addUser } = useFirestore<UserProfile>('users', []);
  
  // Settings & Menu
  const { data: settingsData, add: addSetting, update: updateSetting } = useFirestore<StoreSettings>('store_settings', [{ id: 'general' }]);
  const { data: menuItems, add: addMenuItem, remove: removeMenuItem } = useFirestore<MenuItem>('menu_items', DEFAULT_MENU_ITEMS);

  const storeSettings = settingsData.find(s => s.id === 'general') || { id: 'general' };
  
  // New Hooks for Dynamic Layout
  const { data: flatDeals, add: addFlatDeal, remove: removeFlatDeal } = useFirestore<FlatDeal>('layout_flat_deals', [
      { id: '1', price: '2,999', label: 'Everything', bgColor: '#4a00e0' },
      { id: '2', price: '3,999', label: 'Everything', bgColor: '#4a00e0' },
      { id: '3', price: '4,999', label: 'Everything', bgColor: '#4a00e0' },
      { id: '4', price: '5,999', label: 'Everything', bgColor: '#4a00e0' }
  ]);
  const { data: topCategories, add: addTopCategory, remove: removeTopCategory } = useFirestore<TopCategory>('layout_top_categories', [
      { id: 'men', title: 'MEN', subtitle: 'UPTO 70% OFF', imageUrl: 'https://images.unsplash.com/photo-1491553895911-0055eca6402d?auto=format&fit=crop&q=80&w=400', linkCategory: 'Men' },
      { id: 'women', title: 'WOMEN', subtitle: 'UPTO 85% OFF', imageUrl: 'https://images.unsplash.com/photo-1543163521-1bf539c55dd2?auto=format&fit=crop&q=80&w=400', linkCategory: 'Women' },
      { id: 'kids', title: 'KIDS', subtitle: 'UPTO 80% OFF', imageUrl: 'https://images.unsplash.com/photo-1514989940723-e8875ea6ab51?auto=format&fit=crop&q=80&w=400', linkCategory: 'Kids' }
  ]);

  // --- Local State ---
  const [cartItems, setCartItems] = usePersistedState<CartItem[]>('shoebhai_cart', []);
  const [view, setView] = usePersistedState<'home' | 'admin' | 'login'>('shoebhai_view', 'home');
  const [loggedInUser, setLoggedInUser] = usePersistedState<UserProfile | null>('shoebhai_user_profile', null);
  
  // Admin Navigation State
  const [adminInitialTab, setAdminInitialTab] = useState<'dashboard' | 'products' | 'categories' | 'orders' | 'layout' | 'reports' | 'promotions' | 'reviews' | 'settings'>('dashboard');

  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  
  // 'Home' means showing the landing page with banners/categories. 
  const [selectedCategory, setSelectedCategory] = useState<string>('Home'); 
  
  const [isOrderStatusOpen, setIsOrderStatusOpen] = useState(false);
  const [orderSearchId, setOrderSearchId] = useState('');
  const [lastPlacedOrder, setLastPlacedOrder] = useState<Order | null>(null);
  
  // Login/Register Form State
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [registerForm, setRegisterForm] = useState({ username: '', email: '', phone: '', password: '' });
  const [isRegistering, setIsRegistering] = useState(false);
  const [isAdminAuthMode, setIsAdminAuthMode] = useState(false); // New State to separate Admin Login
  const [authError, setAuthError] = useState('');

  // --- Seed Data Helper ---
  const seedData = async () => {
     try {
        for(const p of MOCK_PRODUCTS) await addProduct(p);
        for(const b of INITIAL_BANNERS) await addBanner(b);
        for(const c of DEFAULT_CATEGORIES) await addCategory(c);
        for(const m of DEFAULT_MENU_ITEMS) await addMenuItem(m);
        alert("Data seeded!");
     } catch (e) {
        console.error(e);
        alert("Error seeding data.");
     }
  };

  // --- Cart Actions ---
  const addToCart = (product: Product, size: string, color: string, quantity = 1, openCart = true) => {
    setCartItems(prev => {
      const safePrev = Array.isArray(prev) ? prev.filter(i => i) : [];
      const existing = safePrev.find(item => item.id === product.id && item.selectedSize === size && item.selectedColor === color);
      if (existing) {
        return safePrev.map(item => 
          item.id === product.id && item.selectedSize === size && item.selectedColor === color
            ? { ...item, quantity: item.quantity + quantity } 
            : item
        );
      }
      return [...safePrev, { ...product, quantity: quantity, selectedSize: size, selectedColor: color }];
    });
    if (openCart) setIsCartOpen(true);
  };

  const removeFromCart = (productId: string, size: string, color: string) => {
    setCartItems(prev => (Array.isArray(prev) ? prev : []).filter(item => item && !(item.id === productId && item.selectedSize === size && item.selectedColor === color)));
  };

  const updateQuantity = (productId: string, size: string, color: string, quantity: number) => {
    setCartItems(prev => (Array.isArray(prev) ? prev : []).map(item => 
      item && item.id === productId && item.selectedSize === size && item.selectedColor === color ? { ...item, quantity } : item
    ));
  };

  const clearCart = () => setCartItems([]);

  const handleBuyNow = (product: Product, size: string, color: string, quantity: number = 1) => {
      addToCart(product, size, color, quantity, false);
      setSelectedProduct(null);
      setIsCheckoutOpen(true);
  };

  const cartTotal = useMemo(() => {
    const safeItems = Array.isArray(cartItems) ? cartItems.filter(i => i) : [];
    return safeItems.reduce((total, item) => total + ((item.discountPrice || item.price || 0) * (item.quantity || 1)), 0);
  }, [cartItems]);

  const itemCount = useMemo(() => {
      const safeItems = Array.isArray(cartItems) ? cartItems.filter(i => i) : [];
      return safeItems.reduce((c, i) => c + (i.quantity || 0), 0);
  }, [cartItems]);

  const cartContextValue = {
    items: Array.isArray(cartItems) ? cartItems.filter(i => i) : [],
    addToCart: (p: Product, s: string, c: string, q: number = 1) => addToCart(p, s, c, q, true),
    removeFromCart,
    updateQuantity,
    clearCart,
    cartTotal,
    itemCount,
    isCartOpen,
    setIsCartOpen
  };

  // --- Order Logic ---
  const placeOrder = async (orderData: any) => {
    const newOrder: Order = {
      id: Math.floor(100000 + Math.random() * 900000).toString(),
      ...orderData,
      status: 'Pending',
      date: new Date().toISOString()
    };
    await addOrder(newOrder);
    clearCart();
    setIsCheckoutOpen(false);
    setIsCartOpen(false);
    setLastPlacedOrder(newOrder);
  };

  const addReview = async (newReview: Review) => {
    await addReviewFirebase(newReview);
  };

  // --- Auth Logic ---
  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');

    if (isAdminAuthMode) {
        // ADMIN LOGIN LOGIC
        if (loginForm.username === 'admin' && loginForm.password === 'admin123') {
            const adminUser: UserProfile = { 
                id: 'admin', username: 'Administrator', email: 'admin@shoebhai.pk', phone: '', password: '', role: 'admin', createdAt: '' 
            };
            setLoggedInUser(adminUser);
            setView('admin');
        } else {
            setAuthError("Invalid Admin Credentials.");
        }
        return;
    }

    if (isRegistering) {
        // CUSTOMER REGISTRATION
        if (!registerForm.username || !registerForm.email || !registerForm.phone || !registerForm.password) {
            setAuthError("Please fill all fields.");
            return;
        }
        
        // Simple check if user exists
        const exists = users.find(u => u.username === registerForm.username || u.email === registerForm.email);
        if (exists) {
            setAuthError("Username or Email already exists.");
            return;
        }

        const newUser: UserProfile = {
            id: Date.now().toString(),
            username: registerForm.username,
            email: registerForm.email,
            phone: registerForm.phone,
            password: registerForm.password, // Demo only
            role: 'user',
            createdAt: new Date().toISOString()
        };

        await addUser(newUser);
        setLoggedInUser(newUser);
        setRegisterForm({ username: '', email: '', phone: '', password: '' });
        setIsRegistering(false);
        setView('home');
    } else {
        // CUSTOMER LOGIN
        const user = users.find(u => u.username === loginForm.username && u.password === loginForm.password);
        if (user) {
            setLoggedInUser(user);
            setView('home');
        } else {
            setAuthError("Invalid username or password.");
        }
    }
  };

  const handleLogout = () => {
      setLoggedInUser(null);
      setView('home');
      setLoginForm({ username: '', password: '' });
      setIsAdminAuthMode(false);
      setAdminInitialTab('dashboard');
  };

  const safeProducts = Array.isArray(products) ? products : [];
  
  // Filter products based on category selection
  const filteredProducts = selectedCategory === 'Home' || selectedCategory === 'All'
  ? safeProducts.filter(p => p.isActive) 
  : safeProducts.filter(p => p.isActive && p.category === selectedCategory);

  // Newest products first
  const latestProducts = [...safeProducts].filter(p => p.isActive).reverse().slice(0, 12);

  const FloatingWhatsApp = () => (
    <a 
      href="https://wa.me/923180508608" 
      target="_blank" 
      rel="noreferrer"
      className="fixed bottom-24 right-4 z-40 bg-[#25D366] text-white p-3 rounded-full shadow-lg hover:bg-[#128C7E] transition-transform hover:scale-110 flex items-center justify-center"
      aria-label="Chat on WhatsApp"
    >
        <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
    </a>
  );

  const BottomNav = () => (
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 py-2 px-6 flex justify-between items-end z-50 text-[10px] font-medium text-gray-500">
          <button className="flex flex-col items-center gap-1 hover:text-black">
              <Ruler size={22} strokeWidth={1.5} />
              <span>Size Guide</span>
          </button>
          <button onClick={() => setIsOrderStatusOpen(true)} className="flex flex-col items-center gap-1 hover:text-black">
              <MapPin size={22} strokeWidth={1.5} />
              <span>Track Order</span>
          </button>
           <button className="flex flex-col items-center gap-1 hover:text-black">
              <RotateCcw size={22} strokeWidth={1.5} />
              <span>Return Pol...</span>
          </button>
           <button onClick={() => setView('login')} className="flex flex-col items-center gap-1 hover:text-black">
              <User size={22} strokeWidth={1.5} />
              <span>{loggedInUser ? 'Account' : 'Login'}</span>
          </button>
      </div>
  );

  if (view === 'admin') {
    return (
      <AdminDashboard 
        products={products}
        orders={orders}
        banners={banners}
        reviews={reviews}
        categories={categories}
        flatDeals={flatDeals}
        topCategories={topCategories}
        promoCodes={promoCodes}
        storeSettings={storeSettings}
        menuItems={menuItems}
        onAddProduct={addProduct}
        onUpdateProduct={updateProduct}
        onDeleteProduct={removeProduct}
        onUpdateOrder={updateOrder}
        onAddCategory={addCategory}
        onDeleteCategory={removeCategory}
        onAddBanner={addBanner}
        onDeleteBanner={removeBanner}
        onAddFlatDeal={addFlatDeal}
        onDeleteFlatDeal={removeFlatDeal}
        onAddTopCategory={addTopCategory}
        onDeleteTopCategory={removeTopCategory}
        onAddPromoCode={addPromoCode}
        onDeletePromoCode={removePromoCode}
        onUpdatePromoCode={updatePromoCode}
        onDeleteReview={removeReview}
        onUpdateStoreSettings={updateSetting}
        onAddMenuItem={addMenuItem}
        onDeleteMenuItem={removeMenuItem}
        onLogout={handleLogout}
        seedData={seedData}
        initialTab={adminInitialTab}
      />
    );
  }

  if (view === 'login') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <div className="bg-white p-8 rounded-xl shadow-xl max-w-md w-full animate-fade-in">
           {/* HEADER */}
           <h2 className="text-3xl font-bold mb-2 text-center text-gray-900">
               {isAdminAuthMode ? 'Admin Portal' : (isRegistering ? 'Create Account' : 'Welcome Back')}
           </h2>
           <p className="text-center text-gray-500 mb-6">
               {isAdminAuthMode 
                 ? 'Secure access for store management' 
                 : (isRegistering ? 'Join Shoebhai for exclusive deals' : 'Login to access your orders')
               }
           </p>
           
           {authError && (
               <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm mb-4 text-center font-medium border border-red-100">
                   {authError}
               </div>
           )}

           <form onSubmit={handleAuth} className="space-y-4">
               {/* ADMIN LOGIN FORM */}
               {isAdminAuthMode ? (
                   <>
                       <div className="relative">
                            <ShieldCheck className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18}/>
                            <input type="text" required className="w-full border-2 border-gray-200 rounded-xl p-3 pl-10 focus:border-black outline-none transition-colors" placeholder="Admin Username" value={loginForm.username} onChange={e => setLoginForm({...loginForm, username: e.target.value})} />
                       </div>
                       <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18}/>
                            <input type="password" required className="w-full border-2 border-gray-200 rounded-xl p-3 pl-10 focus:border-black outline-none transition-colors" placeholder="Admin Password" value={loginForm.password} onChange={e => setLoginForm({...loginForm, password: e.target.value})} />
                       </div>
                       <button type="submit" className="w-full bg-gray-900 text-white py-4 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-black transition-colors shadow-lg shadow-gray-300">
                           <Lock size={18} /> Access Dashboard
                       </button>
                   </>
               ) : (
                   /* CUSTOMER LOGIN/REGISTER FORM */
                   <>
                       {isRegistering ? (
                           <>
                               <div className="relative">
                                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18}/>
                                    <input type="text" required className="w-full border-2 border-gray-200 rounded-xl p-3 pl-10 focus:border-black outline-none transition-colors" placeholder="Username" value={registerForm.username} onChange={e => setRegisterForm({...registerForm, username: e.target.value})} />
                               </div>
                               <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18}/>
                                    <input type="email" required className="w-full border-2 border-gray-200 rounded-xl p-3 pl-10 focus:border-black outline-none transition-colors" placeholder="Email Address" value={registerForm.email} onChange={e => setRegisterForm({...registerForm, email: e.target.value})} />
                               </div>
                               <div className="relative">
                                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18}/>
                                    <input type="tel" required className="w-full border-2 border-gray-200 rounded-xl p-3 pl-10 focus:border-black outline-none transition-colors" placeholder="Phone Number" value={registerForm.phone} onChange={e => setRegisterForm({...registerForm, phone: e.target.value})} />
                               </div>
                               <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18}/>
                                    <input type="password" required className="w-full border-2 border-gray-200 rounded-xl p-3 pl-10 focus:border-black outline-none transition-colors" placeholder="Password" value={registerForm.password} onChange={e => setRegisterForm({...registerForm, password: e.target.value})} />
                               </div>
                           </>
                       ) : (
                           <>
                               <div className="relative">
                                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18}/>
                                    <input type="text" required className="w-full border-2 border-gray-200 rounded-xl p-3 pl-10 focus:border-black outline-none transition-colors" placeholder="Username" value={loginForm.username} onChange={e => setLoginForm({...loginForm, username: e.target.value})} />
                               </div>
                               <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18}/>
                                    <input type="password" required className="w-full border-2 border-gray-200 rounded-xl p-3 pl-10 focus:border-black outline-none transition-colors" placeholder="Password" value={loginForm.password} onChange={e => setLoginForm({...loginForm, password: e.target.value})} />
                               </div>
                           </>
                       )}
                       
                       <button type="submit" className="w-full bg-black text-white py-4 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-gray-800 transition-colors shadow-lg shadow-gray-200">
                           {isRegistering ? 'Register Now' : 'Login'}
                       </button>
                   </>
               )}
           </form>

           <div className="mt-6 text-center text-sm">
               {!isAdminAuthMode && (
                   <>
                       <span className="text-gray-500">{isRegistering ? 'Already have an account?' : "Don't have an account?"}</span>
                       <button onClick={() => { setIsRegistering(!isRegistering); setAuthError(''); }} className="text-black font-bold ml-2 underline">
                           {isRegistering ? 'Login Here' : "Create Account"}
                       </button>
                       <br/>
                   </>
               )}
               
               <button onClick={() => setView('home')} className="mt-6 text-gray-400 hover:text-gray-600 text-xs flex items-center justify-center gap-1 mx-auto">
                   <ArrowRight size={12} className="rotate-180"/> Back to Store
               </button>

               <div className="mt-8 border-t pt-4">
                   <button 
                    onClick={() => { setIsAdminAuthMode(!isAdminAuthMode); setAuthError(''); setLoginForm({username:'', password:''}); }} 
                    className="text-xs text-gray-400 hover:text-gray-600 flex items-center justify-center gap-1 mx-auto"
                   >
                       {isAdminAuthMode ? 'Return to Customer Login' : 'Admin Login'}
                   </button>
               </div>
           </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar 
        cart={cartContextValue} 
        onOpenMenu={() => setIsMenuOpen(true)} 
        logoUrl={storeSettings.logoUrl} 
        onCategoryClick={(cat) => { setSelectedCategory(cat); setIsMenuOpen(false); }}
      />
      
      <main className="pb-24">
         {/* Show different layout based on whether we are on "Home" or viewing a Category */}
         {selectedCategory === 'Home' ? (
             <>
                 {/* Hero Banners - Now Dynamic */}
                 <HeroSlider banners={banners} />
                 
                 {/* Flat Deals Section - Now Dynamic */}
                 {flatDeals && flatDeals.length > 0 && (
                     <div className="mt-8 text-center px-4">
                         <h2 className="text-xl font-bold uppercase tracking-wide mb-6">FLAT DEALS</h2>
                         <div className="grid grid-cols-2 gap-2">
                             {flatDeals.map((deal) => (
                                 <div 
                                    key={deal.id} 
                                    onClick={()=>setSelectedCategory('All')} 
                                    className="text-white p-4 flex flex-col items-center justify-center h-24 cursor-pointer hover:opacity-90 transition-opacity"
                                    style={{ backgroundColor: deal.bgColor || '#4a00e0' }}
                                 >
                                     <span className="text-xs uppercase tracking-wider">{deal.label}</span>
                                     <span className="font-bold text-xl md:text-2xl font-mono">Rs {deal.price}</span>
                                 </div>
                             ))}
                         </div>
                     </div>
                 )}

                 {/* Top Categories - Now Dynamic */}
                 {topCategories && topCategories.length > 0 && (
                     <div className="mt-10 text-center px-4">
                         <h2 className="text-xl font-bold uppercase tracking-wide mb-6">TOP CATEGORY</h2>
                         <div className="grid grid-cols-3 gap-2">
                             {topCategories.map((cat) => (
                                 <div key={cat.id} onClick={() => setSelectedCategory(cat.linkCategory)} className="bg-[#4a00e0] group cursor-pointer relative overflow-hidden h-64 md:h-80 flex flex-col">
                                     <div className="p-3 text-white z-10 text-left">
                                         <h3 className="font-bold text-lg leading-tight uppercase">{cat.title}</h3>
                                         <div className="bg-yellow-400 text-black text-[10px] font-bold px-1 py-0.5 inline-block mt-1 uppercase">
                                             {cat.subtitle}
                                         </div>
                                     </div>
                                     <img src={cat.imageUrl} className="absolute bottom-0 right-0 w-4/5 object-contain transform group-hover:scale-110 transition-transform duration-500" alt={cat.title} />
                                     <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/grid-me.png')] opacity-10 pointer-events-none"></div>
                                 </div>
                             ))}
                         </div>
                     </div>
                 )}

                 {/* Clearance Sale Banner */}
                 <div className="mt-8 bg-[#4a00e0] text-white py-3 px-4 flex justify-between items-center overflow-hidden cursor-pointer" onClick={()=>setSelectedCategory('All')}>
                     <div className="flex flex-col">
                         <span className="bg-yellow-400 text-black text-xs font-bold px-2 py-0.5 self-start mb-1">12.12</span>
                         <span className="font-bold text-lg tracking-wider">CLEARANCE SALE</span>
                     </div>
                     <div className="text-right">
                         <span className="block text-yellow-400 text-xs font-bold">BUY 2</span>
                         <span className="block text-white text-xs font-bold">GET 1 FREE</span>
                     </div>
                 </div>

                 {/* REAL DYNAMIC PRODUCT GRID */}
                 <div className="mt-10 px-4">
                     <h2 className="text-xl font-bold uppercase tracking-wide mb-6 text-center">FRESH ARRIVALS</h2>
                     {latestProducts.length > 0 ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {latestProducts.map(product => (
                                <ProductCard key={product.id} product={product} onClick={(p) => setSelectedProduct(p)} />
                            ))}
                        </div>
                     ) : (
                         <div className="text-center py-10 bg-gray-100 rounded-lg text-gray-500">
                             <Loader2 className="animate-spin mx-auto mb-2" />
                             Loading products...
                         </div>
                     )}
                     <div className="mt-8 text-center">
                        <button onClick={() => setSelectedCategory('All')} className="bg-black text-white px-8 py-3 rounded-full font-bold uppercase tracking-wider text-sm hover:bg-gray-800 transition-colors">
                            View All Products
                        </button>
                     </div>
                 </div>

                 {/* Store Returns Banner */}
                 <div className="mt-10 bg-[#4a00e0] text-white p-6 mx-4 mb-8 rounded-t-lg relative overflow-hidden cursor-pointer" onClick={()=>setSelectedCategory('Returns')}>
                     <h3 className="text-2xl font-cursive italic z-10 relative">Store Returns</h3>
                     <p className="text-xs uppercase mt-1 z-10 relative">STARTING PRICE</p>
                     <p className="text-xl font-bold z-10 relative">Rs. 1,799</p>
                     <img src="https://images.unsplash.com/photo-1606107557195-0e29a4b5b4aa?auto=format&fit=crop&q=80&w=400" className="absolute right-0 bottom-[-20px] w-1/2 object-contain" />
                 </div>

             </>
         ) : (
             /* CATEGORY GRID VIEW */
             <div className="max-w-7xl mx-auto px-4 mt-4 min-h-screen">
                 <div className="flex items-center gap-2 mb-6">
                     <button onClick={() => setSelectedCategory('Home')} className="text-sm font-bold flex items-center gap-1 hover:underline">
                         Home
                     </button>
                     <span className="text-gray-400">/</span>
                     <span className="text-sm font-bold text-blue-600">{selectedCategory}</span>
                 </div>
                 
                 <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold uppercase">{selectedCategory}</h2>
                    <span className="text-xs text-gray-500">{filteredProducts.length} Products</span>
                 </div>

                 {filteredProducts.length === 0 ? (
                     <div className="text-center py-20">
                         <p className="text-gray-500">No products found in this category.</p>
                         <button onClick={()=>setSelectedCategory('Home')} className="mt-4 text-blue-600 underline">Go Back Home</button>
                     </div>
                 ) : (
                     <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                         {filteredProducts.map(product => (
                             <ProductCard key={product.id} product={product} onClick={(p) => setSelectedProduct(p)} />
                         ))}
                     </div>
                 )}
             </div>
         )}
         
         <Footer />
      </main>

      <FloatingWhatsApp />
      <BottomNav />
      
      <MenuDrawer 
        isOpen={isMenuOpen} 
        onClose={() => setIsMenuOpen(false)} 
        onLoginClick={() => setView('login')} 
        onCategoryClick={(cat) => { setSelectedCategory(cat); setIsMenuOpen(false); }} 
        onTrackOrderClick={() => { setIsOrderStatusOpen(true); setIsMenuOpen(false); }} 
        categories={categories}
        menuItems={menuItems}
        isAdmin={loggedInUser?.role === 'admin'}
        onAdminClick={(tab) => { setAdminInitialTab(tab); setView('admin'); }}
      />
      
      <CartSidebar cart={cartContextValue} onCheckout={() => { setIsCartOpen(false); setIsCheckoutOpen(true); }} />
      
      {selectedProduct && (
          <ProductDetail 
            product={selectedProduct} 
            onClose={() => setSelectedProduct(null)} 
            onAddToCart={addToCart} 
            onBuyNow={handleBuyNow} 
            reviews={reviews} 
            onAddReview={addReview} 
            userProfile={loggedInUser} 
            orders={orders}
          />
      )}
      
      {isCheckoutOpen && (
          <CheckoutModal 
            cart={cartContextValue} 
            onClose={() => setIsCheckoutOpen(false)} 
            onPlaceOrder={placeOrder} 
            promoCodes={promoCodes}
            currentUser={loggedInUser}
          />
      )}
      
      <OrderTrackingModal isOpen={isOrderStatusOpen} onClose={() => setIsOrderStatusOpen(false)} orders={orders} searchId={orderSearchId} setSearchId={setOrderSearchId} />
      
      {lastPlacedOrder && (
        <div className="fixed inset-0 z-[300] bg-black bg-opacity-75 flex items-center justify-center p-4">
            <div className="bg-white p-8 rounded-xl max-w-sm w-full text-center animate-fade-in">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 text-green-600"><CheckCircle size={32} /></div>
                <h2 className="text-2xl font-bold mb-2">Order Placed!</h2>
                <p className="text-gray-600 mb-6">Order #{lastPlacedOrder.id} received.</p>
                <div className="bg-gray-50 p-4 rounded-lg mb-6 text-left text-sm space-y-2">
                    <p className="flex justify-between"><span>Total:</span> <span className="font-bold">Rs. {lastPlacedOrder.total.toLocaleString()}</span></p>
                    {lastPlacedOrder.promoCode && <p className="flex justify-between text-green-600"><span>Promo:</span> <span className="font-bold">{lastPlacedOrder.promoCode}</span></p>}
                </div>
                <button onClick={() => setLastPlacedOrder(null)} className="w-full bg-black text-white py-3 rounded-lg font-bold">Continue Shopping</button>
            </div>
        </div>
      )}
    </div>
  );
}